import * as React from 'react'
import { useCallback } from 'react'

import { displayLabelIfExists } from '@app/domain/instrument/instrument.helpers'
import type {
  IProgram,
  IProgramInteraction,
  IProgramInteractionId,
} from '@app/domain/program/program.interface'
import { ProgramInteractionTypeId } from '@app/domain/program/program.interface'

import {
  InteractionLabel,
  StyledSelectGroup,
  StyledSelectOption,
} from './styled-select-group'

const INITIAL_INTERACTION_LABEL = 'Initial'

export type props = {
  initialInteractionCompletedDate: Date
  program: IProgram
  programInteractionHistory: Record<IProgramInteractionId, Date>
  isDisabled?: boolean
}

export const InitialInteractionSelectOptionsComponent = ({
  initialInteractionCompletedDate,
  program,
  programInteractionHistory,
  isDisabled,
}: props) => {
  const initialInteractionSelect = useCallback(
    (
      interaction: IProgramInteraction,
      programInteractionHistory: Record<IProgramInteractionId, Date>,
    ) => {
      const initialInteractionIsCompleted =
        initialInteractionCompletedDate !== undefined
      if (
        interaction.interactionType ===
        ProgramInteractionTypeId.InitialInteraction
      ) {
        const instruments = interaction.instruments.map(displayLabelIfExists)

        const shouldStrikeThrough = !isDisabled && initialInteractionIsCompleted
        const description = programInteractionHistory?.[interaction.id]
          ? `${instruments.join(', ')} (Completed)`
          : instruments.join(', ')

        return (
          <StyledSelectOption
            key={interaction.id}
            value={interaction.id}
            description={description}
            isDisabled={initialInteractionIsCompleted}
          >
            <InteractionLabel $strikeThrough={shouldStrikeThrough}>
              {INITIAL_INTERACTION_LABEL}
            </InteractionLabel>
          </StyledSelectOption>
        )
      } else {
        return null
      }
    },
    [initialInteractionCompletedDate, isDisabled],
  )

  if (program.interactions[ProgramInteractionTypeId.InitialInteraction]) {
    return (
      <StyledSelectGroup label="Initial Interactions">
        {program.interactions[ProgramInteractionTypeId.InitialInteraction].map(
          (interaction) =>
            initialInteractionSelect(interaction, programInteractionHistory),
        )}
      </StyledSelectGroup>
    )
  } else {
    return null
  }
}

export const initialInteractionSelectOptions = function (
  initialInteractionCompletedDate: Date,
  program: IProgram,
  programInteractionHistory: Record<IProgramInteractionId, Date>,
  isDisabled?: boolean,
) {
  return InitialInteractionSelectOptionsComponent({
    initialInteractionCompletedDate,
    program,
    programInteractionHistory,
    isDisabled,
  })
}
